import { Col } from '@amzn/stencil-react-components/layout';
import { H3, Text } from '@amzn/stencil-react-components/text';
import { Spinner, SpinnerSize } from '@amzn/stencil-react-components/spinner';

export interface GeneratingQuestionsStateProps {
  /** Headline, e.g. "Astra is generating your questions…". */
  readonly title: string;
  /** Supporting line under the headline (sets expectations: takes ~a minute). */
  readonly description: string;
  /** Accessible loading text for the spinner (screen-reader announcement). */
  readonly loadingText: string;
  readonly dataTestId?: string;
}

/**
 * In-page "Astra is generating…" state for the Create Questions screen.
 *
 * Rendered in place of the prompt card while a generation request is in flight,
 * so the page header/title stays visible (NOT a full-viewport takeover — the
 * user keeps their context). A Stencil `Spinner` plus a title and a
 * expectation-setting description communicate that the 60–90s generation is
 * working. No Astra icon here — the page header already carries the Astra brand
 * mark, so repeating it would double the sparkle.
 *
 * Presentational only — all copy is passed in (i18n lives at the call site).
 */
export function GeneratingQuestionsState({
  title,
  description,
  loadingText,
  dataTestId,
}: GeneratingQuestionsStateProps): JSX.Element {
  return (
    <Col
      alignItems="center"
      gridGap="dimensions.spacing.300"
      padding="dimensions.spacing.500"
      width="100%"
      {...(dataTestId !== undefined ? { dataTestId } : {})}
    >
      <Spinner size={SpinnerSize.Medium} loadingText={loadingText} showText={false} />
      <Col alignItems="center" gridGap="dimensions.spacing.100">
        <H3>{title}</H3>
        <Text color="color.text.secondary">{description}</Text>
      </Col>
    </Col>
  );
}
